import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	friendRemove,
	friendRequestRespond,
	friendsListRequest,
	pendingRequestsRequest,
} from '../../../store/friends/friendsActions';
import {
	selectFriendActionError,
	selectFriendBusyIds,
	selectFriendsList,
	selectPendingRequests,
} from '../../../store/friends/friendsReducer';
import { IndicatorScrollView, SCROLL_INDICATOR } from '../common/ScrollIndicator';
import { PANEL_WIDTH } from '../Inventory/inventoryTheme';
import FriendRow from './FriendRow';
import { Friend, friendFromDirectory, friendFromRequest } from './friendsCatalog';
import FriendsInvite from './FriendsInvite';
import FriendsSearch from './FriendsSearch';
import {
	BODY_PADDING,
	BODY_TOP,
	FRIENDS_FONT,
	friendsColors,
	SCROLL_BAR,
	SECTION_GAP,
} from './friendsTheme';

/**
 * Figma "Body" (4244:41082) of "Modal / Friends" (4244:41069) - everything the modal shows while
 * the Friends tab is selected. The header above it belongs to the modal and does not change.
 *
 * The whole column scrolls, not just the list: the file's scrolling frame is "Friends / Full
 * content" (4244:41083), 1054 tall against a 785 viewport, and it carries the invite card and the
 * search row with it. The indicator down the right edge spans the same body.
 */
/** The design's content column ends 20 below the last row. */
const CONTENT_BOTTOM = 20;

type Props = {
	scale: number;
	/** The panel's height in design units - the body is whatever is left under the header. */
	panelHeight: number;
	onInvite?: () => void;
	/** There is no gift service yet; the row's gift button shows only once this is passed. */
	onSendGift?: (friend: Friend) => void;
};

/**
 * The list is the player's friends from gateway-game `/api/friends`, searched on the server.
 * Incoming friend requests sit above it, since the design has no place of their own for them.
 */
export const FriendsTab = ({ scale, panelHeight, onInvite, onSendGift }: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const friendsList = useSelector(selectFriendsList);
	const pending = useSelector(selectPendingRequests);
	const actionError = useSelector(selectFriendActionError);
	const busyPlayerIds = useSelector(selectFriendBusyIds);

	const styles = useMemo(() => createStyles(scale, panelHeight), [scale, panelHeight]);

	useEffect(() => {
		dispatch(pendingRequestsRequest());
	}, [dispatch]);

	useEffect(() => {
		dispatch(friendsListRequest({ query }));
	}, [dispatch, query]);

	const friends = friendsList.items.map(friendFromDirectory);
	const requests = query.trim() ? [] : pending.items;

	const emptyText = (() => {
		if (friends.length) return null;
		if (friendsList.loading) {
			return t('T_SITE_FRIENDS_LOADING', { defaultValue: 'Loading friends...' });
		}
		if (friendsList.errorKey) return friendsList.errorKey;
		if (query.trim()) {
			return t('T_SITE_FRIENDS_NO_MATCH', { defaultValue: 'No friends match that name.' });
		}
		return t('T_SITE_FRIENDS_EMPTY_TAB', {
			defaultValue: 'No friends yet. Find players on the Social screen and send them a request.',
		});
	})();

	return (
		// "Scroll track" / "Scroll thumb" (4244:41455/41456): the shared design bar, at x=350 in
		// the body and 8 clear of its top and bottom. The body's 12 of padding already leaves it room.
		<IndicatorScrollView
			style={styles.body}
			contentContainerStyle={styles.content}
			keyboardShouldPersistTaps='handled'
			indicatorInsets={{
				top: SCROLL_BAR.inset * scale,
				bottom: SCROLL_BAR.inset * scale,
				right: (PANEL_WIDTH - SCROLL_BAR.x) * scale - SCROLL_INDICATOR.width,
			}}
			reserveGutter={false}
		>
			<FriendsInvite scale={scale} onInvite={onInvite} />

			<FriendsSearch
				value={query}
				onChangeText={setQuery}
				friendsCount={friendsList.friendsCount}
				scale={scale}
			/>

			{actionError ? <Text style={styles.error}>{actionError.message}</Text> : null}

			{requests.length ? (
				<View style={styles.list}>
					<Text style={styles.sectionLabel}>
						{t('T_SITE_FRIENDS_REQUESTS_COUNT', {
							defaultValue: 'Friend requests ({{count}})',
							count: requests.length,
						})}
					</Text>
					{requests.map(request => (
						<FriendRow
							key={request.id}
							friend={friendFromRequest(request)}
							scale={scale}
							statusText={t('T_SITE_FRIENDS_WANTS_TO_BE_FRIEND', {
								defaultValue: 'Wants to be your friend',
							})}
							busy={!!busyPlayerIds[request.requesterId]}
							onAccept={() =>
								dispatch(
									friendRequestRespond({
										playerId: request.requesterId,
										requestId: request.id,
										accept: true,
									}),
								)
							}
							onReject={() =>
								dispatch(
									friendRequestRespond({
										playerId: request.requesterId,
										requestId: request.id,
										accept: false,
									}),
								)
							}
						/>
					))}
				</View>
			) : null}

			<View style={styles.list}>
				{emptyText ? <Text style={styles.empty}>{emptyText}</Text> : null}
				{friends.map(friend => (
					<FriendRow
						key={friend.id}
						friend={friend}
						scale={scale}
						busy={!!busyPlayerIds[friend.id]}
						onSendGift={onSendGift}
						onRemove={() => dispatch(friendRemove({ playerId: friend.id }))}
					/>
				))}
			</View>
		</IndicatorScrollView>
	);
};

const createStyles = (scale: number, panelHeight: number) =>
	StyleSheet.create({
		body: {
			position: 'absolute',
			left: 0,
			top: BODY_TOP * scale,
			width: PANEL_WIDTH * scale,
			height: (panelHeight - BODY_TOP) * scale,
		},
		content: {
			paddingHorizontal: BODY_PADDING * scale,
			paddingTop: BODY_PADDING * scale,
			paddingBottom: CONTENT_BOTTOM * scale,
			gap: SECTION_GAP * scale,
		},
		list: {
			gap: 8 * scale,
		},
		sectionLabel: {
			color: friendsColors.gold300,
			fontFamily: FRIENDS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
		},
		empty: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: 13 * scale,
			fontWeight: '500',
			textAlign: 'center',
			paddingVertical: 12 * scale,
		},
		error: {
			color: '#FF8A7A',
			fontFamily: FRIENDS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			textAlign: 'center',
		},
	});

export default FriendsTab;
